import { useEffect, useState } from "react";
import { DownloadSimple, Database, FileCsv, Code, ShieldCheck, Clock, Warning, Buildings } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";

// Flat + versioned collection list. Keeps the UI in lockstep with the
// backend COLLECTIONS registry in /app/backend/routes_export.py — if you add
// a new entity there, add it here too for the CSV downloads.
const CSV_ENTITIES = [
    { slug: "clients",                 label: "Clients" },
    { slug: "leads",                   label: "Leads" },
    { slug: "proposals",               label: "Proposals (parent)" },
    { slug: "proposals_versions",      label: "Proposals · versions" },
    { slug: "proposals_line_items",    label: "Proposals · line items" },
    { slug: "pos",                     label: "Purchase Orders (parent)" },
    { slug: "pos_versions",            label: "POs · versions" },
    { slug: "pos_line_items",          label: "POs · line items" },
    { slug: "invoices",                label: "Invoices" },
    { slug: "payments",                label: "Payments" },
    { slug: "credit_notes",            label: "Credit Notes" },
    { slug: "audit_logs",              label: "Audit Logs" },
    { slug: "users",                   label: "Users (no password hashes)" },
    { slug: "counters",                label: "Counters (number sequences)" },
    { slug: "amc_schedules",           label: "AMC Schedules" },
    { slug: "invoice_events",          label: "Invoice Events" },
    { slug: "follow_ups",              label: "Follow-ups" },
    { slug: "catalogue_items",         label: "Catalogue Items" },
    { slug: "proposal_templates",      label: "Proposal Templates" },
    { slug: "proposal_tnc_master",     label: "T&C Clause Library" },
    { slug: "email_templates",         label: "Email Templates" },
];

// Download a blob as a timestamped file. Runs entirely in-browser — no
// backend storage involved.
const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const todayStamp = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
};

export default function DataExport() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(null); // slug of entity currently downloading
    const [downloadingFull, setDownloadingFull] = useState(false);
    const [downloadingSchema, setDownloadingSchema] = useState(false);
    const [downloadingRepo, setDownloadingRepo] = useState(false);

    useEffect(() => {
        // Preview the counts via the dashboard summary + a `/full` tiny
        // peek (fetched with HEAD-like no-op). We just reuse dashboard counts
        // for the at-a-glance tiles — full export is the actual action.
        api.get("/billing/dashboard/summary")
            .then(({ data }) => setSummary(data))
            .catch((err) => toast.error(formatApiError(err, "Failed to load export summary")))
            .finally(() => setLoading(false));
    }, []);

    const downloadFullSnapshot = async () => {
        setDownloadingFull(true);
        try {
            const { data } = await api.get("/export/full");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            saveBlob(blob, `reedos-snapshot-${todayStamp()}.json`);
            toast.success("Full snapshot downloaded");
        } catch (err) {
            toast.error(formatApiError(err, "Export failed"));
        } finally {
            setDownloadingFull(false);
        }
    };

    const downloadSchema = async () => {
        setDownloadingSchema(true);
        try {
            const { data } = await api.get("/export/schema");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            saveBlob(blob, `reedos-schema-${todayStamp()}.json`);
            toast.success("Schema downloaded");
        } catch (err) {
            toast.error(formatApiError(err, "Schema export failed"));
        } finally {
            setDownloadingSchema(false);
        }
    };

    const downloadCsv = async (slug, label) => {
        setDownloading(slug);
        try {
            const { data } = await api.get(`/export/${slug}.csv`, { responseType: "blob" });
            saveBlob(data, `reedos-${slug}-${todayStamp()}.csv`);
            toast.success(`${label} CSV downloaded`);
        } catch (err) {
            toast.error(formatApiError(err, `Failed to export ${label}`));
        } finally {
            setDownloading(null);
        }
    };

    // Phase 2.3 — surface the existing Company Repository bundle endpoint
    // (mapping.csv + logos/ + manifest.json + README.txt) here so the
    // Data Export page is the single, consistent place for migration /
    // backup / DR / vendor-independence extractions. Backend endpoint and
    // RBAC unchanged.
    const downloadRepoBundle = async () => {
        setDownloadingRepo(true);
        try {
            const { data } = await api.get("/data/admin/export", { responseType: "blob" });
            saveBlob(data, `reedos-companies-repo-${todayStamp()}.zip`);
            toast.success("Company Repository bundle downloaded");
        } catch (err) {
            toast.error(formatApiError(err, "Company Repository export failed"));
        } finally {
            setDownloadingRepo(false);
        }
    };

    return (
        <div data-testid="data-export-page">
                <PageHeader
                    eyebrow="Administration"
                    title="Data Export"
                    description="Migration-ready snapshots. All exports are read-only, audit-safe, and preserve every ID, timestamp and audit trail byte-for-byte."
                    icon={<Database size={18} />}
                />
                <div className="px-6 sm:px-8 py-8 space-y-8 max-w-6xl">
                    {/* Hero action — Full Snapshot */}
                    <section className="reedos-card p-6">
                        <div className="flex flex-wrap items-start justify-between gap-6">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <Database size={18} className="text-slate-900" />
                                    <h2 className="text-base font-semibold text-slate-900">Full Snapshot (JSON)</h2>
                                    <span className="reedos-eyebrow text-[9px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">Recommended</span>
                                </div>
                                <p className="text-sm text-slate-600 leading-relaxed max-w-xl">
                                    One-click bundle of every collection — <span className="font-medium">clients, leads, proposals, POs, invoices, payments, credit notes, audit logs, users, counters</span> and more. Use this as your baseline before the eventual .NET + SQL migration, or as a nightly backup.
                                </p>
                                <div className="mt-3 text-[11px] text-slate-500 flex flex-wrap items-center gap-3">
                                    <span className="inline-flex items-center gap-1"><ShieldCheck size={11} /> password hashes excluded</span>
                                    <span className="inline-flex items-center gap-1"><Clock size={11} /> timestamps preserved (ISO 8601 UTC)</span>
                                    <span className="inline-flex items-center gap-1"><Code size={11} /> IDs unchanged</span>
                                </div>
                            </div>
                            <Button
                                onClick={downloadFullSnapshot}
                                disabled={downloadingFull}
                                size="lg"
                                className="bg-slate-900 hover:bg-slate-800 text-white"
                                data-testid="export-full-snapshot-btn"
                            >
                                <DownloadSimple size={16} className="mr-2" />
                                {downloadingFull ? "Preparing…" : "Download Full Snapshot"}
                            </Button>
                        </div>
                    </section>

                    {/* Counts at a glance */}
                    {summary && (
                        <section className="reedos-card p-6">
                            <h3 className="text-sm font-semibold text-slate-900 mb-4">Current data volume</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                                <CountTile label="Clients" value={summary.po?.total_clients ?? summary.clients?.total} />
                                <CountTile label="Purchase Orders" value={summary.po?.total} />
                                <CountTile label="Invoices" value={summary.invoice?.total} />
                                <CountTile label="Payments" value={summary.payment?.total} />
                                <CountTile label="Proposals" value={summary.proposal?.total ?? 0} />
                            </div>
                            {loading && <div className="text-xs text-slate-500 mt-3">Loading…</div>}
                        </section>
                    )}

                    {/* Schema export */}
                    <section className="reedos-card p-6">
                        <div className="flex flex-wrap items-start justify-between gap-6">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <Code size={18} className="text-slate-900" />
                                    <h2 className="text-base font-semibold text-slate-900">Schema Definition (JSON)</h2>
                                </div>
                                <p className="text-sm text-slate-600 leading-relaxed max-w-xl">
                                    Machine-readable schema — field types, foreign keys, relationships, and sample SQL DDL. Hand this to your .NET / SQL team and they can stand up the target tables in a day.
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                onClick={downloadSchema}
                                disabled={downloadingSchema}
                                data-testid="export-schema-btn"
                            >
                                <DownloadSimple size={14} className="mr-2" />
                                {downloadingSchema ? "Preparing…" : "Download Schema"}
                            </Button>
                        </div>
                    </section>

                    {/* Phase 2.3 — Company Repository bundle (reuses existing
                        GET /api/data/admin/export endpoint) */}
                    <section className="reedos-card p-6" data-testid="company-repo-export-section">
                        <div className="flex flex-wrap items-start justify-between gap-6">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <Buildings size={18} className="text-slate-900" />
                                    <h2 className="text-base font-semibold text-slate-900">Company Repository (ZIP)</h2>
                                </div>
                                <p className="text-sm text-slate-600 leading-relaxed max-w-xl">
                                    Full Company Repository snapshot including <span className="font-medium">mapping.csv</span>, logo assets, <span className="font-medium">manifest.json</span> and <span className="font-medium">README.txt</span>. Suitable for migration, backup, disaster recovery and vendor-independent archival.
                                </p>
                                <div className="mt-3 text-[11px] text-slate-500 flex flex-wrap items-center gap-3">
                                    <span className="inline-flex items-center gap-1"><ShieldCheck size={11} /> identifiers preserved</span>
                                    <span className="inline-flex items-center gap-1"><Clock size={11} /> last_updated_at preserved (ISO 8601 UTC)</span>
                                    <span className="inline-flex items-center gap-1"><Code size={11} /> UTF-8 BOM CSV (Excel-compatible)</span>
                                </div>
                            </div>
                            <Button
                                onClick={downloadRepoBundle}
                                disabled={downloadingRepo}
                                size="lg"
                                data-testid="export-company-repo-bundle-btn"
                            >
                                <DownloadSimple size={16} className="mr-2" />
                                {downloadingRepo ? "Preparing…" : "Download Company Repository Bundle"}
                            </Button>
                        </div>
                    </section>

                    {/* Per-entity CSVs */}
                    <section className="reedos-card p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <FileCsv size={18} className="text-slate-900" />
                            <h2 className="text-base font-semibold text-slate-900">Entity-level CSVs</h2>
                            <span className="reedos-eyebrow text-[9px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">for SQL bulk-load</span>
                        </div>
                        <p className="text-xs text-slate-500 mb-4 max-w-2xl">
                            For versioned entities (POs, Proposals), the parent-child split maps 1:1 to relational tables: <code className="text-[11px] bg-slate-100 px-1 rounded">pos.csv</code> → <code className="text-[11px] bg-slate-100 px-1 rounded">pos_versions.csv</code> → <code className="text-[11px] bg-slate-100 px-1 rounded">pos_line_items.csv</code>. Import them in that order with FK constraints enabled.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {CSV_ENTITIES.map((e) => (
                                <button
                                    key={e.slug}
                                    type="button"
                                    onClick={() => downloadCsv(e.slug, e.label)}
                                    disabled={downloading === e.slug}
                                    data-testid={`export-csv-${e.slug}`}
                                    className="group flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors text-left disabled:opacity-50"
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm text-slate-900 truncate">{e.label}</div>
                                        <code className="text-[10px] text-slate-500">{e.slug}.csv</code>
                                    </div>
                                    {downloading === e.slug ? (
                                        <span className="text-[10px] text-slate-500">…</span>
                                    ) : (
                                        <DownloadSimple size={14} className="text-slate-400 group-hover:text-slate-900 shrink-0" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </section>

                    <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded-md px-4 py-3 flex items-start gap-2">
                        <Warning size={14} className="text-amber-600 shrink-0 mt-0.5" />
                        <div>
                            <strong className="text-amber-900">Security note:</strong> exports contain every client, contact, and financial record in the system. Treat downloaded files like production database dumps — store them on encrypted storage, never commit to git, and destroy after the migration round-trip is verified.
                        </div>
                    </div>
                </div>
            </div>
    );
}

const CountTile = ({ label, value }) => (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2.5">
        <div className="reedos-eyebrow text-[9px]">{label}</div>
        <div className="mt-0.5 text-lg font-semibold text-slate-900 tabular-nums" data-testid={`export-count-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            {typeof value === "number" ? value.toLocaleString("en-IN") : "—"}
        </div>
    </div>
);
